import { isBefore, isValidDate } from '@bluebrick/saas-ui'
import { DateTime, type DateTimeUnit, Duration } from 'luxon'
import { toSafeError, toSafeOk, type Safe } from '@/common/safe'

export function fromDateTime(dateTime: DateTime<true>) {
	return <DateTime<true>>DateTime.fromMillis(dateTime.toMillis()).setZone(dateTime.zone)
}

export function fromValidDate(date: Date): DateTime<true> {
	return <DateTime<true>>DateTime.fromJSDate(date).toUTC()
}

export function now() {
	return DateTime.now().toUTC()
}

export function isSameDate(dateA: DateTime, dateB: DateTime) {
	return !!dateA && !!dateB && dateA.isValid && dateB.isValid && dateA.toUTC().hasSame(dateB.toUTC(), 'day')
}

export function isSameDateTime(dateA?: DateTime, dateB?: DateTime) {
	return !!dateA && !!dateB && dateA.isValid && dateB.isValid && dateA.toMillis() === dateB.toMillis()
}

export function isDateTimeBefore(checkedDateTime: DateTime<true>, referenceDateTime: DateTime<true>, orTheSame?: boolean) {
	const isBefore = checkedDateTime.toUTC().startOf('day') < referenceDateTime.toUTC().startOf('day')

	if (orTheSame) {
		return isBefore || isSameDate(checkedDateTime, referenceDateTime)
	}

	return isBefore
}

export function isNewerDateTime(newDate: DateTime | undefined, oldDate: DateTime | undefined) {
	return !!newDate && newDate.isValid && (!oldDate || !oldDate.isValid || newDate.toUTC().valueOf() > oldDate.toUTC().valueOf())
}

export function isDateTimeAfter(checkedDateTime: DateTime<true>, referenceDateTime: DateTime<true>, orTheSame?: boolean) {
	const isAfter = checkedDateTime.toUTC().startOf('day') > referenceDateTime.toUTC().startOf('day')

	if (orTheSame) {
		return isAfter || isSameDate(checkedDateTime, referenceDateTime)
	}

	return isAfter
}

export function monthYearFormattedShort(date: DateTime): string {
	return date.toFormat('MMM yyyy')
}

export function dateTimeFormattedShort(date: DateTime, withTimeZone = false): string {
	const timeZoneSuffix: string = withTimeZone && date.offsetNameShort !== null ? ` ${date.offsetNameShort}` : ''

	return date.toFormat('MMM d, yyyy') + timeZoneSuffix
}

export function dateTimeFormattedShortWithTime(date: DateTime, withTimeZone = false): string {
	const timeZoneSuffix: string = withTimeZone && date.offsetNameShort !== null ? ` (${date.offsetNameShort})` : ''

	return date.toFormat('MMM d, yyyy HH:mm') + timeZoneSuffix
}

export function isWeekend(date: Date): boolean {
	return date.getDay() === 0 || date.getDay() === 6
}

const NOT_A_DATE = 'Not a date'

export function convertToDateTime(date: Date | string | object | undefined, asUTC?: boolean): Safe<DateTime<true>> {
	if (date instanceof Date && isValidDate(date)) {
		if (asUTC) {
			return toSafeOk(DateTime.utc(date.getFullYear(), date.getMonth() + 1, date.getDate()) as DateTime<true>)
		}

		return toSafeOk(fromValidDate(date))
	}

	if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
		// In case of "object" type we check if it's a Moment.js date
		const asDate = date.toDate()

		if (isValidDate(asDate)) {
			if (asUTC) {
				return toSafeOk(DateTime.utc(asDate.getFullYear(), asDate.getMonth() + 1, asDate.getDate()) as DateTime<true>)
			}

			return toSafeOk(fromValidDate(asDate))
		}
	}

	if (typeof date === 'string' && isValidDate(date) && DateTime.fromISO(date).isValid) {
		return toSafeOk(<DateTime<true>>DateTime.fromISO(date))
	}

	return toSafeError(NOT_A_DATE)
}

export function isInPast(date: Date) {
	return isBefore(date, new Date())
}

export function isInPastDateTime(date: DateTime) {
	return date.startOf('day') <= now().startOf('day')
}

/**
 * Mobiscroll timeline uses a convention where end dates point not to the last day of event/range/etc.
 * but to a day **after** the day of range (see https://mobiscroll.com/docs/vue/datepicker/exclusive-end-dates
 * and https://mobiscroll.com/docs/vue/eventcalendar/api#opt-exclusiveEndDates).
 * This concerns all end dates for ranges used within Mobiscroll's timeline (events, dragged ranges,
 * visible time ranges, etc.). To resolve this we could:
 * a) use the `exclusiveEndDates` option. This would cause Mobiscroll to use actual last day of range
 * as the end date. But it would also require us to adjust end dates of all events which we pass to
 * the timeline so that they're rendered correctly\
 * or
 * b) adjust the end dates of ranges returned by Mobiscroll (by moving it one day back) so that they
 * point to the last day of range).
 * Below function is used to achieve option `b)`
 *
 * #mobiscroll-exclusive-end-dates
 *
 * TODO: Check if this is still valid when operating on timestamps and not all day events
 */
export function excludeMobiscrollEndDate(originalEndDate: Date | string | object | undefined): Safe<DateTime<true>> {
	const asDate = convertToDateTime(originalEndDate)

	if (asDate.ok) {
		return toSafeOk(asDate.value.minus({ day: 1 }))
	}

	return toSafeError(NOT_A_DATE)
}

export function daysToUnit(days: number, unit: DateTimeUnit = 'second'): number {
	return Duration.fromDurationLike({ days }).as(unit)
}

export function unitToDays(amountInUnit: number, unit: DateTimeUnit = 'second'): number {
	return Duration.fromObject({ [unit]: amountInUnit }).as('days')
}

export function toISODateStringUTC(date: Date | string | DateTime): string | null {
	if (date instanceof DateTime) {
		return date.toUTC().toISO()
	}

	if (date instanceof Date) {
		return DateTime.fromJSDate(date).toUTC().toISO()
	}

	if (typeof date === 'string') {
		return DateTime.fromISO(date).toUTC().toISO()
	}

	return null
}

/**
 * Sets target's hour part to be the same as source's hour. Makes sure that daylight savings differences
 * between source and target are not impacting the change. So that if source's hour in referenceTimeZone
 * is 9 then returned datetime will also have 9 hour in referenceTimeZone. Returned datetime is kept
 * in target's timezone.
 */
export function setSourcesHourInTarget(source: DateTime, target: DateTime, referenceTimeZone: string) {
	const sourceHourInLocalZone = source.setZone(referenceTimeZone).hour

	return target.setZone(referenceTimeZone).set({ hour: sourceHourInLocalZone }).setZone(target.zone)
}
